/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";

interface SeriesItem {
  name: string;
  data: number[];
}

interface MultipleStackedBarChartProps {
  categories: string[];
  series: SeriesItem[];
  height?: number;
  horizontal?: boolean;
  onBarSliceClick?: (payload: {
    category: string;
    seriesName: string;
    isBreached: boolean;
  }) => void;
}

export default function MultipleStackedBarChart({
  categories,
  series,
  height = 350,
  horizontal = false,
  onBarSliceClick,
}: MultipleStackedBarChartProps) {
  const chartOptions = useMemo<ApexCharts.ApexOptions>(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        events: {
          dataPointSelection: (_e, _ctx, config) => {
            const { seriesIndex, dataPointIndex } = config;

            if (
              seriesIndex == null ||
              dataPointIndex == null ||
              !series[seriesIndex]
            ) {
              return;
            }

            const seriesName = series[seriesIndex].name; // Breached / Non Breached
            const category = categories[dataPointIndex];

            // Prevent ApexCharts DOM crash
            setTimeout(() => {
              onBarSliceClick?.({
                category,
                seriesName,
                isBreached: seriesName === "Breached",
              });
            }, 100);
          },
        },
      },
      colors: ["#60db87", "#e65c66"],
      plotOptions: {
        bar: {
          horizontal,
          columnWidth: horizontal ? undefined : "65%",
          barHeight: horizontal ? "80%" : undefined,
          borderRadius: 4,
        },
      },
      dataLabels: { enabled: true },
      stroke: { width: 1, colors: ["#fff"] },
      xaxis: { categories },
      // yaxis: horizontal
      //   ? {
      //       labels: {
      //         maxWidth: 250,
      //       },
      //     }
      //   : {},

      legend: { position: "top" },
      tooltip: { shared: true, intersect: false },
    }),
    [categories, series, onBarSliceClick],
  );

  const computedHeight = useMemo(() => {
    if (!horizontal) return height;

    const maxVisibleBars = 14;
    const barHeight = 40;

    if (categories.length <= maxVisibleBars) return height;

    return categories.length * barHeight;
  }, [categories.length, horizontal, height]);

  const [state, setState] = useState({
    options: chartOptions,
    series: series.length
      ? series
      : [{ name: "Empty", data: categories.map(() => 0) }],
  });

  useEffect(() => {
    setState({
      options: chartOptions,
      series:
        series.length > 0
          ? series
          : [{ name: "Empty", data: categories.map(() => 0) }],
    });
  }, [series, categories, chartOptions]);

  const maxVisibleBars = 14;

  return (
    <div
      style={{
        maxHeight:
          horizontal && categories.length > maxVisibleBars ? height : "auto",
        overflowY:
          horizontal && categories.length > maxVisibleBars ? "auto" : "visible",
      }}
    >
      <ReactApexChart
        options={state.options}
        series={state.series}
        type="bar"
        height={computedHeight}
      />
    </div>
  );
}
